import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Card, Select, Input, Button, Space, Tag, Alert, Typography, Empty, Segmented, Table, message } from 'antd';
import {
  ScanOutlined, CheckCircleFilled, CloseCircleFilled, LockFilled,
  WarningFilled, PrinterOutlined, TagsOutlined, PlusOutlined, DeleteOutlined,
  ExperimentOutlined
} from '@ant-design/icons';
import { ErpMasterShell } from '../../../../components/common/erp-grid';
import useAuth from '../../../../hooks/useAuth';
import { workOrdersApi } from './workOrdersApi';
import { canScanPieces, canScrapPieces } from './workOrderHelpers';
import { productionDepartmentsApi } from '../../masters/productionDepartments/productionDepartmentsApi';
import { breakageApi } from '../breakage/breakageApi';
import {
  BREAKAGE_REASONS, reasonLabel, hasPermission as hasBreakagePerm, PERM as BREAKAGE_PERM
} from '../breakage/breakageHelpers';
import '../production.css';

// ════════════════════════════════════════════════════════════════════
// Stage WO-1B / WO-1B.2 — Scan Station.
//
// A department operator picks their station, then scans piece barcodes (a
// keyboard-wedge USB/Bluetooth scanner types the 13-char code + Enter):
//   • Advance     — POST /work-orders/scan → moves the piece one route stage.
//                   A 🏷 relabel hint appears when a piece clears wash/temper
//                   (the sticker is destroyed there → reprint the same ref).
//   • Report scrap — POST /work-orders/scrap → marks the piece broken and
//                   auto-issues a REMAKE with a new barcode; a "Print remake
//                   sticker" action prints that one new label.
// The input stays focused for rapid continuous scanning. Read-only elsewhere:
// the board %, status, and stage modal reflect scans automatically. Advance is
// gated on …piece.scan; scrap on …piece.scrap.
// ════════════════════════════════════════════════════════════════════

const { Text } = Typography;

function backendError(err, fallback) {
  return (err && err.response && err.response.data && err.response.data.error && err.response.data.error.message)
    || (err && err.message) || fallback || 'Scan failed.';
}
function deptCode(d) { return String(d.department_code || d.code || d.departmentCode || '').toUpperCase(); }
function deptName(d) { return d.department_name || d.name || d.departmentName || deptCode(d); }
// "COMPONENT 2/4" / "3/5" — kind-aware piece position from a scan/scrap result.
function pieceLabel(res) {
  const prefix = res.pieceKind === 'SINGLE' ? '' : `${res.pieceKind} `;
  return `${prefix}${res.pieceIndex}/${res.pieceTotal}`;
}

export default function WorkOrderScanPage() {
  const { me } = useAuth();
  const perms = me && me.permissions;
  const mayScan = canScanPieces(perms);
  const mayScrap = canScrapPieces(perms);
  const mayBreakage = hasBreakagePerm(perms, BREAKAGE_PERM.REQUEST);

  const [mode, setMode] = useState('advance');       // 'advance' | 'scrap'
  const [depts, setDepts] = useState([]);
  const [dept, setDept] = useState(null);
  const [reason, setReason] = useState('');
  const [barcode, setBarcode] = useState('');
  const [busy, setBusy] = useState(false);
  const [log, setLog] = useState([]);
  const inputRef = useRef(null);
  const seqRef = useRef(0);

  const scrapMode = mode === 'scrap';

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const rows = await productionDepartmentsApi.executionFlow();
        const list = (Array.isArray(rows) ? rows : (rows && rows.rows) || [])
          .map((d) => ({ code: deptCode(d), name: deptName(d) }))
          .filter((d) => d.code);
        if (alive) setDepts(list);
      } catch (_e) { if (alive) setDepts([]); }
    })();
    return () => { alive = false; };
  }, []);

  const deptOptions = useMemo(
    () => depts.map((d) => ({ value: d.code, label: `${d.name} · ${d.code}` })),
    [depts]
  );
  const deptLabel = useMemo(() => {
    const d = depts.find((x) => x.code === dept);
    return d ? d.name : null;
  }, [depts, dept]);

  const focusInput = useCallback(() => { if (inputRef.current) inputRef.current.focus(); }, []);

  // Reprint ONE piece's label (remake / relabel). Opens the rendered HTML in a
  // new tab for printing — kept off the scan path so it never steals focus.
  const printSticker = useCallback(async (code) => {
    try {
      const html = await workOrdersApi.pieceSticker(code);
      const url = URL.createObjectURL(new Blob([html], { type: 'text/html' }));
      const win = window.open(url, '_blank');
      if (!win) message.warning('Allow pop-ups to print the sticker.');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { message.error(backendError(e, 'Could not load the sticker.')); }
  }, []);

  const submit = useCallback(async () => {
    const code = (barcode || '').trim();
    if (!code || busy) return;
    if (scrapMode) {
      if (!reason.trim()) { message.warning('Enter a scrap reason first.'); return; }
    } else if (!dept) {
      message.warning('Select your department station first.'); return;
    }
    setBusy(true);
    const id = ++seqRef.current;
    try {
      if (scrapMode) {
        const res = await workOrdersApi.scrap({ barcode: code, reason: reason.trim() });
        setLog((l) => [{
          id, ok: true, scrap: true, barcode: res.scrappedBarcode, woNo: res.woNo,
          piece: pieceLabel(res), stage: res.scrappedStage,
          remake: res.remakeBarcode, reason: res.reason
        }, ...l].slice(0, 60));
      } else {
        const res = await workOrdersApi.scan({ barcode: code, departmentCode: dept });
        const done = res.productionState === 'COMPLETED';
        const tail = done ? '— COMPLETED' : (res.nextStage ? `→ ${res.nextStage.name}` : '');
        setLog((l) => [{
          id, ok: true, barcode: res.barcode, woNo: res.woNo,
          piece: pieceLabel(res), stage: res.completedStage && res.completedStage.name,
          tail, locked: res.locked, done, relabel: res.relabel
        }, ...l].slice(0, 60));
      }
      setBarcode('');
    } catch (err) {
      setLog((l) => [{ id, ok: false, barcode: code, text: backendError(err, scrapMode ? 'Scrap failed.' : 'Scan failed.') }, ...l].slice(0, 60));
      setBarcode('');
    } finally { setBusy(false); focusInput(); }
  }, [barcode, dept, reason, scrapMode, busy, focusInput]);

  const stats = useMemo(
    () => ({ ok: log.filter((x) => x.ok).length, err: log.filter((x) => !x.ok).length }),
    [log]
  );

  if (!mayScan && !mayScrap && !mayBreakage) {
    return (
      <ErpMasterShell>
        <div className="erp-production-page">
          <Alert type="warning" showIcon message="You don't have permission to scan pieces." />
        </div>
      </ErpMasterShell>
    );
  }

  const canSubmit = barcode.trim() && (scrapMode ? reason.trim() : dept);

  return (
    <ErpMasterShell>
      <div className="erp-production-page">
        {(mayScan || mayScrap) ? (
        <Card
          className="erp-wo-scan-console"
          title={<Space><ScanOutlined /> <span>Scan Station</span></Space>}
          extra={
            <Space size="middle">
              <Text type="secondary" style={{ fontSize: 12 }}>Session</Text>
              <Tag color="green" style={{ marginInlineEnd: 0 }}>{stats.ok} ok</Tag>
              {stats.err ? <Tag color="red" style={{ marginInlineEnd: 0 }}>{stats.err} error</Tag> : null}
            </Space>
          }
        >
          {mayScan && mayScrap ? (
            <Segmented
              className="erp-wo-scan-mode"
              value={mode}
              onChange={(v) => { setMode(v); focusInput(); }}
              options={[
                { label: 'Advance', value: 'advance', icon: <ScanOutlined /> },
                { label: 'Report scrap', value: 'scrap', icon: <WarningFilled /> }
              ]}
            />
          ) : null}

          <div className="erp-wo-scan-controls">
            {scrapMode ? (
              <div className="erp-wo-scan-field">
                <Text type="secondary" className="erp-wo-scan-label">Scrap reason</Text>
                <Input
                  size="large"
                  allowClear
                  placeholder="e.g. Corner chip, scratch, breakage"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  onPressEnter={focusInput}
                />
              </div>
            ) : (
              <div className="erp-wo-scan-field">
                <Text type="secondary" className="erp-wo-scan-label">Department station</Text>
                <Select
                  showSearch optionFilterProp="label"
                  placeholder="Select your station"
                  style={{ width: '100%' }}
                  value={dept}
                  onChange={(v) => { setDept(v); focusInput(); }}
                  options={deptOptions}
                  notFoundContent="No route-stage departments"
                />
              </div>
            )}
            <div className="erp-wo-scan-field erp-wo-scan-field--grow">
              <Text type="secondary" className="erp-wo-scan-label">Scan barcode</Text>
              <Input
                ref={inputRef}
                size="large"
                autoFocus
                allowClear
                inputMode="text"
                prefix={<ScanOutlined style={{ color: '#94a3b8' }} />}
                placeholder={scrapMode
                  ? (reason.trim() ? 'Scan the broken piece, then Enter' : 'Enter a reason first')
                  : (dept ? 'Scan or type a piece barcode, then Enter' : 'Select a station first')}
                value={barcode}
                disabled={scrapMode ? !reason.trim() : !dept}
                onChange={(e) => setBarcode(e.target.value)}
                onPressEnter={submit}
              />
            </div>
            <Button
              type="primary"
              danger={scrapMode}
              size="large"
              icon={scrapMode ? <WarningFilled /> : <ScanOutlined />}
              loading={busy}
              onClick={submit}
              disabled={!canSubmit}
            >
              {scrapMode ? 'Scrap piece' : 'Scan'}
            </Button>
          </div>

          {scrapMode ? (
            <Alert type="warning" showIcon className="mt-2"
              message={<span>Scrapping marks the piece <strong>broken</strong> and issues a <strong>remake with a new barcode</strong>. Print and apply the new sticker before it continues.</span>} />
          ) : dept ? (
            <Alert type="info" showIcon className="mt-2"
              message={<span>Scanning at <strong>{deptLabel}</strong>. Each scan advances that piece to its next stage.</span>} />
          ) : null}

          <div className="erp-production-section-title">Recent scans</div>
          {log.length === 0 ? (
            <Empty description="No scans yet" image={Empty.PRESENTED_IMAGE_SIMPLE} />
          ) : (
            <div className="erp-wo-scan-log">
              {log.map((row) => (
                <div key={row.id} className={`erp-wo-scan-row erp-wo-scan-row--${row.ok ? (row.scrap ? 'scrap' : 'ok') : 'err'}`}>
                  <span className="erp-wo-scan-row__icon">
                    {!row.ok ? <CloseCircleFilled /> : row.scrap ? <WarningFilled /> : <CheckCircleFilled />}
                  </span>
                  <code className="erp-grid__code erp-wo-scan-row__code">{row.barcode}</code>
                  {!row.ok ? (
                    <span className="erp-wo-scan-row__body erp-wo-scan-row__error">{row.text}</span>
                  ) : row.scrap ? (
                    <span className="erp-wo-scan-row__body">
                      <strong>{row.woNo}</strong>
                      <span className="erp-wo-scan-row__muted"> · {row.piece}</span>
                      <span className="erp-wo-scan-row__stage"> · Scrapped{row.stage ? ` at ${row.stage}` : ''}</span>
                      <span className="erp-wo-scan-row__muted"> · remake </span>
                      <code className="erp-grid__code">{row.remake}</code>
                      <Button type="link" size="small" className="erp-wo-scan-row__print"
                        icon={<PrinterOutlined />} onClick={() => printSticker(row.remake)}>
                        Print remake sticker
                      </Button>
                    </span>
                  ) : (
                    <span className="erp-wo-scan-row__body">
                      <strong>{row.woNo}</strong>
                      <span className="erp-wo-scan-row__muted"> · {row.piece}</span>
                      <span className="erp-wo-scan-row__stage"> · {row.stage} {row.tail}</span>
                      {row.locked ? <LockFilled className="erp-wo-scan-row__lock" title="Piece locked at cutting" /> : null}
                      {row.relabel ? (
                        <>
                          <Tag icon={<TagsOutlined />} color="gold" className="erp-wo-scan-row__relabel">Relabel</Tag>
                          <Button type="link" size="small" className="erp-wo-scan-row__print"
                            icon={<PrinterOutlined />} onClick={() => printSticker(row.barcode)}>
                            Reprint sticker
                          </Button>
                        </>
                      ) : null}
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </Card>
        ) : null}

        {mayBreakage ? <BreakagePanel /> : null}
      </div>
    </ErpMasterShell>
  );
}

// ════════════════════════════════════════════════════════════════════
// Report Breakage — an operator builds a breakage slip by adding one or more
// broken pieces (barcode + department + reason + optional remarks) to an
// in-memory list, then submits it as a single rework request. The backend
// returns the slip no; approval (with the L1 MFA gate + auto-remake) happens
// on the Breakage Approvals page. Gated on erp.production.workorder.breakage.request.
// ════════════════════════════════════════════════════════════════════
function BreakagePanel() {
  const [mains, setMains] = useState([]);
  const [barcode, setBarcode] = useState('');
  const [dept, setDept] = useState(null);
  const [reasonCode, setReasonCode] = useState(null);
  const [remarks, setRemarks] = useState('');
  const [items, setItems] = useState([]);
  const [slipRemarks, setSlipRemarks] = useState('');
  const [busy, setBusy] = useState(false);
  const barcodeRef = useRef(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const data = await productionDepartmentsApi.lookups();
        const list = (data && Array.isArray(data.mains) ? data.mains : [])
          .map((d) => ({ code: deptCode(d), name: deptName(d) }))
          .filter((d) => d.code);
        if (alive) setMains(list);
      } catch (_e) { if (alive) setMains([]); }
    })();
    return () => { alive = false; };
  }, []);

  const deptOptions = useMemo(
    () => mains.map((d) => ({ value: d.code, label: `${d.name} · ${d.code}` })),
    [mains]
  );
  const reasonOptions = useMemo(
    () => BREAKAGE_REASONS.map((r) => ({ value: r.code, label: r.label })),
    []
  );

  const focusBarcode = useCallback(() => { if (barcodeRef.current) barcodeRef.current.focus(); }, []);

  const addItem = useCallback(() => {
    const code = (barcode || '').trim();
    if (!code) { message.warning('Enter a piece barcode.'); return; }
    if (!dept) { message.warning('Select the department.'); return; }
    if (!reasonCode) { message.warning('Select a breakage reason.'); return; }
    setItems((l) => [...l, {
      key: `${code}-${l.length}-${Date.now()}`,
      barcode: code, departmentCode: dept, reasonCode, remarks: (remarks || '').trim() || undefined
    }]);
    setBarcode('');
    setRemarks('');
    focusBarcode();
  }, [barcode, dept, reasonCode, remarks, focusBarcode]);

  const removeItem = useCallback((key) => {
    setItems((l) => l.filter((x) => x.key !== key));
  }, []);

  const submit = useCallback(async () => {
    if (!items.length) { message.warning('Add at least one broken piece.'); return; }
    setBusy(true);
    try {
      const payload = {
        items: items.map((x) => ({
          barcode: x.barcode, departmentCode: x.departmentCode,
          reasonCode: x.reasonCode, remarks: x.remarks
        })),
        remarks: (slipRemarks || '').trim() || undefined
      };
      const res = await breakageApi.report(payload);
      message.success(`Breakage request raised · ${res && res.slipNo ? res.slipNo : 'slip created'} (awaiting approval).`);
      setItems([]);
      setSlipRemarks('');
      focusBarcode();
    } catch (err) {
      message.error(backendError(err, 'Could not raise the breakage request.'));
    } finally { setBusy(false); }
  }, [items, slipRemarks, focusBarcode]);

  const itemColumns = useMemo(() => [
    { title: 'Barcode', dataIndex: 'barcode', key: 'barcode', render: (v) => <code className="erp-grid__code">{v}</code> },
    { title: 'Department', dataIndex: 'departmentCode', key: 'departmentCode' },
    { title: 'Reason', dataIndex: 'reasonCode', key: 'reasonCode', render: (v) => reasonLabel(v) },
    { title: 'Remarks', dataIndex: 'remarks', key: 'remarks', render: (v) => v || <span className="text-slate-300">—</span> },
    {
      title: '', key: 'remove', width: 48, align: 'center',
      render: (_v, r) => <Button type="text" size="small" danger icon={<DeleteOutlined />} onClick={() => removeItem(r.key)} />
    }
  ], [removeItem]);

  const canAdd = barcode.trim() && dept && reasonCode;

  return (
    <Card
      className="mt-4"
      title={<Space><ExperimentOutlined /> <span>Report Breakage</span></Space>}
      extra={<Text type="secondary" style={{ fontSize: 12 }}>{items.length} on slip</Text>}
    >
      <div className="erp-wo-scan-controls">
        <div className="erp-wo-scan-field erp-wo-scan-field--grow">
          <Text type="secondary" className="erp-wo-scan-label">Piece barcode</Text>
          <Input
            ref={barcodeRef}
            allowClear
            prefix={<ScanOutlined style={{ color: '#94a3b8' }} />}
            placeholder="Scan or type the broken piece barcode"
            value={barcode}
            onChange={(e) => setBarcode(e.target.value)}
            onPressEnter={addItem}
          />
        </div>
        <div className="erp-wo-scan-field">
          <Text type="secondary" className="erp-wo-scan-label">Department</Text>
          <Select
            showSearch optionFilterProp="label"
            placeholder="Where it broke"
            style={{ minWidth: 200 }}
            value={dept}
            onChange={setDept}
            options={deptOptions}
            notFoundContent="No departments"
          />
        </div>
        <div className="erp-wo-scan-field">
          <Text type="secondary" className="erp-wo-scan-label">Reason</Text>
          <Select
            placeholder="Breakage reason"
            style={{ minWidth: 180 }}
            value={reasonCode}
            onChange={setReasonCode}
            options={reasonOptions}
          />
        </div>
        <div className="erp-wo-scan-field erp-wo-scan-field--grow">
          <Text type="secondary" className="erp-wo-scan-label">Remarks (optional)</Text>
          <Input
            allowClear
            placeholder="Optional note for this piece"
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            onPressEnter={addItem}
          />
        </div>
        <Button icon={<PlusOutlined />} onClick={addItem} disabled={!canAdd}>Add to slip</Button>
      </div>

      <div className="erp-production-section-title">Pieces on this slip</div>
      {items.length === 0 ? (
        <Empty description="No pieces added yet" image={Empty.PRESENTED_IMAGE_SIMPLE} />
      ) : (
        <Table
          size="small"
          rowKey="key"
          columns={itemColumns}
          dataSource={items}
          pagination={false}
        />
      )}

      <div className="mt-3 flex flex-wrap items-end gap-3">
        <div className="erp-wo-scan-field erp-wo-scan-field--grow" style={{ minWidth: 260 }}>
          <Text type="secondary" className="erp-wo-scan-label">Slip remarks (optional)</Text>
          <Input
            allowClear
            placeholder="Optional note for the whole slip"
            value={slipRemarks}
            onChange={(e) => setSlipRemarks(e.target.value)}
          />
        </div>
        <Button
          type="primary"
          icon={<WarningFilled />}
          loading={busy}
          onClick={submit}
          disabled={!items.length}
        >
          Submit breakage request
        </Button>
      </div>

      <Alert type="info" showIcon className="mt-3"
        message={<span>A breakage request is raised as <strong>Pending</strong>. Remakes are issued only after it is <strong>approved</strong> (MFA-gated) on the Breakage Approvals page.</span>} />
    </Card>
  );
}
